import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Fragment, useState } from 'react';

type FeedbackCategory = 'bug' | 'feature_request' | 'other' | 'ai_content_report';
type FeedbackStatus = 'new' | 'triaged' | 'closed';
type AiContentReportReason = 'sexual' | 'hate' | 'harmful' | 'medical_misinfo' | 'other';

interface FeedbackSubmission {
  id: string;
  category: FeedbackCategory;
  status: FeedbackStatus;
  message: string;
  contactEmail: string | null;
  appVersion: string | null;
  platform: string | null;
  osVersion: string | null;
  deviceModel: string | null;
  locale: string | null;
  technicalLogs: string | null;
  aiReportReason: AiContentReportReason | null;
  reportedContent: string | null;
  aiProvider: string | null;
  aiMessageId: string | null;
  createdAt: string;
  updatedAt: string;
}

const CATEGORY_OPTIONS: Array<{ value: '' | FeedbackCategory; label: string }> = [
  { value: '', label: 'All categories' },
  { value: 'bug', label: 'Bug' },
  { value: 'feature_request', label: 'Feature request' },
  { value: 'other', label: 'Other' },
  { value: 'ai_content_report', label: 'AI content report' },
];

const STATUS_OPTIONS: Array<{ value: '' | FeedbackStatus; label: string }> = [
  { value: '', label: 'All statuses' },
  { value: 'new', label: 'New' },
  { value: 'triaged', label: 'Triaged' },
  { value: 'closed', label: 'Closed' },
];

const STATUS_STYLES: Record<FeedbackStatus, string> = {
  new: 'bg-blue-50 text-blue-700 ring-blue-600/20',
  triaged: 'bg-yellow-50 text-yellow-800 ring-yellow-600/20',
  closed: 'bg-gray-50 text-gray-600 ring-gray-500/20',
};

function labelForCategory(category: FeedbackCategory): string {
  switch (category) {
    case 'feature_request':
      return 'Feature request';
    case 'bug':
      return 'Bug';
    case 'ai_content_report':
      return 'AI content report';
    default:
      return 'Other';
  }
}

function previewText(submission: FeedbackSubmission): string {
  if (submission.category === 'ai_content_report') {
    return `[${submission.aiReportReason ?? 'report'}] ${
      submission.reportedContent || submission.message
    }`;
  }
  return submission.message;
}

function primaryMessage(submission: FeedbackSubmission): string {
  if (submission.category === 'ai_content_report') {
    return submission.reportedContent || submission.message;
  }
  return submission.message;
}

function deviceSummary(submission: FeedbackSubmission): string {
  return (
    [
      submission.platform,
      submission.osVersion,
      submission.deviceModel,
      submission.appVersion,
    ]
      .filter(Boolean)
      .join(' · ') || '—'
  );
}

async function fetchFeedback(category: string, status: string) {
  const params = new URLSearchParams();
  if (category) {
    params.set('category', category);
  }
  if (status) {
    params.set('status', status);
  }
  const query = params.toString();
  const response = await fetch(`/api/v1/admin/feedback${query ? `?${query}` : ''}`);
  if (!response.ok) {
    throw new Error('Failed to fetch feedback');
  }
  return response.json() as Promise<{ submissions: FeedbackSubmission[] }>;
}

async function updateFeedbackStatus(id: string, status: FeedbackStatus) {
  const response = await fetch(`/api/v1/admin/feedback/${id}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ status }),
  });
  if (!response.ok) {
    throw new Error('Failed to update feedback status');
  }
  return response.json();
}

export default function FeedbackPage() {
  const queryClient = useQueryClient();
  const [categoryFilter, setCategoryFilter] = useState<'' | FeedbackCategory>('');
  const [statusFilter, setStatusFilter] = useState<'' | FeedbackStatus>('');
  const [expanded, setExpanded] = useState<string | null>(null);

  const { data, isLoading, error } = useQuery({
    queryKey: ['admin-feedback', categoryFilter, statusFilter],
    queryFn: () => fetchFeedback(categoryFilter, statusFilter),
  });

  const updateMutation = useMutation({
    mutationFn: (input: { id: string; status: FeedbackStatus }) =>
      updateFeedbackStatus(input.id, input.status),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-feedback'] });
    },
  });

  const toggleExpanded = (id: string) => {
    setExpanded((current) => (current === id ? null : id));
  };

  const setTriaged = (id: string) => updateMutation.mutate({ id, status: 'triaged' });
  const setClosed = (id: string) => updateMutation.mutate({ id, status: 'closed' });
  const setNew = (id: string) => updateMutation.mutate({ id, status: 'new' });

  const newCount = data?.submissions.filter((submission) => submission.status === 'new').length ?? 0;

  return (
    <div>
      <div className="flex flex-col gap-4 sm:flex-row sm:justify-between sm:items-end">
        <div>
          <h2 className="text-2xl font-bold leading-7 text-gray-900 sm:truncate sm:text-3xl sm:tracking-tight">
            Feedback
          </h2>
          <p className="mt-2 text-sm text-gray-500">
            Triage in-app user feedback and AI content reports
            {data ? ` · ${data.submissions.length} shown` : ''}
            {newCount > 0 ? ` · ${newCount} new` : ''}
          </p>
        </div>
        <div className="flex gap-3">
          <select
            value={categoryFilter}
            onChange={(event) => setCategoryFilter(event.target.value as '' | FeedbackCategory)}
            className="rounded-md border-gray-300 shadow-sm border px-2 py-2 text-sm"
          >
            {CATEGORY_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
          <select
            value={statusFilter}
            onChange={(event) => setStatusFilter(event.target.value as '' | FeedbackStatus)}
            className="rounded-md border-gray-300 shadow-sm border px-2 py-2 text-sm"
          >
            {STATUS_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="mt-6 bg-white shadow rounded-lg overflow-hidden">
        {isLoading ? (
          <div className="text-center py-12">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
            <p className="mt-2 text-sm text-gray-500">Loading feedback...</p>
          </div>
        ) : error ? (
          <div className="p-6">
            <div className="rounded-md bg-red-50 p-4">
              <p className="text-sm text-red-800">
                Error loading feedback: {error.message}
              </p>
            </div>
          </div>
        ) : !data || data.submissions.length === 0 ? (
          <div className="p-6 text-center text-gray-500">
            <p className="mt-2 text-sm">No feedback submissions found</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full table-fixed divide-y divide-gray-300">
              <colgroup>
                <col className="w-[32%]" />
                <col className="w-[12%]" />
                <col className="w-[8%]" />
                <col className="w-[16%]" />
                <col className="w-[14%]" />
                <col className="w-[10%]" />
                <col className="w-[8%]" />
              </colgroup>
              <thead className="bg-gray-50">
                <tr>
                  <th scope="col" className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900">
                    Message
                  </th>
                  <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                    Category
                  </th>
                  <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                    Status
                  </th>
                  <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                    Contact
                  </th>
                  <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                    Device
                  </th>
                  <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                    Received
                  </th>
                  <th scope="col" className="relative py-3.5 pl-3 pr-4 sm:pr-6">
                    <span className="sr-only">Actions</span>
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 bg-white">
                {data.submissions.map((submission) => {
                  const isExpanded = expanded === submission.id;
                  return (
                    <Fragment key={submission.id}>
                      <tr className={isExpanded ? 'bg-indigo-50/40' : 'hover:bg-gray-50'}>
                        <td className="py-4 pl-4 pr-3 text-sm text-gray-900">
                          <button
                            type="button"
                            className="block w-full min-w-0 text-left"
                            aria-expanded={isExpanded}
                            onClick={() => toggleExpanded(submission.id)}
                          >
                            <p className="truncate font-medium" title={previewText(submission)}>
                              {previewText(submission)}
                            </p>
                          </button>
                        </td>
                        <td className="truncate px-3 py-4 text-sm text-gray-500">
                          {labelForCategory(submission.category)}
                        </td>
                        <td className="px-3 py-4 text-sm text-gray-500">
                          <span className={`inline-flex items-center rounded-md px-2 py-1 text-xs font-medium ring-1 ring-inset ${STATUS_STYLES[submission.status]}`}>
                            {submission.status}
                          </span>
                        </td>
                        <td className="truncate px-3 py-4 text-sm text-gray-500" title={submission.contactEmail ?? undefined}>
                          {submission.contactEmail || '—'}
                        </td>
                        <td className="truncate px-3 py-4 text-sm text-gray-500" title={deviceSummary(submission)}>
                          {deviceSummary(submission)}
                        </td>
                        <td className="truncate px-3 py-4 text-sm text-gray-500">
                          {new Date(submission.createdAt).toLocaleString()}
                        </td>
                        <td className="py-4 pl-3 pr-4 text-right text-sm font-medium sm:pr-6">
                          <button
                            type="button"
                            onClick={() => toggleExpanded(submission.id)}
                            className="text-indigo-600 hover:text-indigo-900"
                          >
                            {isExpanded ? 'Hide' : 'Open'}
                          </button>
                        </td>
                      </tr>
                      {isExpanded && (
                        <tr className="bg-slate-50">
                          <td colSpan={7} className="px-4 py-5 sm:px-6">
                            <FeedbackDetails
                              submission={submission}
                              updating={updateMutation.isPending}
                              onTriaged={() => setTriaged(submission.id)}
                              onClosed={() => setClosed(submission.id)}
                              onReopen={() => setNew(submission.id)}
                            />
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

function FeedbackDetails({
  submission,
  updating,
  onTriaged,
  onClosed,
  onReopen,
}: {
  submission: FeedbackSubmission;
  updating: boolean;
  onTriaged: () => void;
  onClosed: () => void;
  onReopen: () => void;
}) {
  const isAiReport = submission.category === 'ai_content_report';

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <p className="text-xs font-medium uppercase tracking-wide text-gray-500">
            {isAiReport ? 'Reported AI response' : 'Full feedback'}
          </p>
          <p className="mt-1 text-sm text-gray-500">
            {labelForCategory(submission.category)}
            {submission.contactEmail ? ` · ${submission.contactEmail}` : ''}
            {` · ${new Date(submission.createdAt).toLocaleString()}`}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          {submission.status !== 'triaged' && (
            <button
              type="button"
              onClick={onTriaged}
              disabled={updating}
              className="rounded-md bg-white px-3 py-1.5 text-sm font-medium text-indigo-700 ring-1 ring-inset ring-indigo-200 hover:bg-indigo-50 disabled:opacity-50"
            >
              Triage
            </button>
          )}
          {submission.status !== 'closed' && (
            <button
              type="button"
              onClick={onClosed}
              disabled={updating}
              className="rounded-md bg-white px-3 py-1.5 text-sm font-medium text-gray-700 ring-1 ring-inset ring-gray-200 hover:bg-gray-50 disabled:opacity-50"
            >
              Close
            </button>
          )}
          {submission.status !== 'new' && (
            <button
              type="button"
              onClick={onReopen}
              disabled={updating}
              className="rounded-md bg-white px-3 py-1.5 text-sm font-medium text-gray-700 ring-1 ring-inset ring-gray-200 hover:bg-gray-50 disabled:opacity-50"
            >
              Reopen
            </button>
          )}
        </div>
      </div>

      <div className="rounded-lg border border-gray-200 bg-white p-5 shadow-sm">
        <p className="whitespace-pre-wrap break-words text-[15px] leading-7 text-gray-900">
          {primaryMessage(submission)}
        </p>
      </div>

      {isAiReport && (
        <div>
          <h3 className="text-sm font-semibold text-gray-900">Notes</h3>
          <div className="mt-2 rounded-lg border border-gray-200 bg-white p-4">
            <p className="whitespace-pre-wrap break-words text-sm leading-6 text-gray-800">
              {submission.message}
            </p>
          </div>
        </div>
      )}

      <div className="grid gap-5 lg:grid-cols-2">
        <div>
          <h3 className="text-sm font-semibold text-gray-900">Diagnostics</h3>
          <dl className="mt-2 grid grid-cols-[8.5rem_minmax(0,1fr)] gap-x-4 gap-y-1.5 text-sm">
            <dt className="text-gray-500">App version</dt>
            <dd className="break-words text-gray-900">{submission.appVersion || '—'}</dd>
            <dt className="text-gray-500">Platform</dt>
            <dd className="break-words text-gray-900">{submission.platform || '—'}</dd>
            <dt className="text-gray-500">OS version</dt>
            <dd className="break-words text-gray-900">{submission.osVersion || '—'}</dd>
            <dt className="text-gray-500">Device</dt>
            <dd className="break-words text-gray-900">{submission.deviceModel || '—'}</dd>
            <dt className="text-gray-500">Locale</dt>
            <dd className="break-words text-gray-900">{submission.locale || '—'}</dd>
            {isAiReport && (
              <>
                <dt className="text-gray-500">Reason</dt>
                <dd className="break-words text-gray-900">{submission.aiReportReason || '—'}</dd>
                <dt className="text-gray-500">AI provider</dt>
                <dd className="break-words text-gray-900">{submission.aiProvider || '—'}</dd>
                <dt className="text-gray-500">Message ID</dt>
                <dd className="break-all text-gray-900">{submission.aiMessageId || '—'}</dd>
              </>
            )}
          </dl>
        </div>
        <div>
          <h3 className="text-sm font-semibold text-gray-900">Technical logs</h3>
          {submission.technicalLogs ? (
            <pre className="mt-2 max-h-72 overflow-auto whitespace-pre-wrap break-words rounded-md bg-gray-900 p-4 text-xs leading-5 text-gray-100">
              {submission.technicalLogs}
            </pre>
          ) : (
            <p className="mt-2 text-sm text-gray-500">No technical logs attached.</p>
          )}
        </div>
      </div>
    </div>
  );
}
